/**
 * DoctorSubscriptionCard — top-of-dashboard subscription status card.
 *
 * Compact card that surfaces current subscription state to doctors and
 * gives them a single click to (a) start a trial if not yet subscribed
 * or (b) open the Stripe Billing Portal to manage an existing one.
 *
 * Renders nothing for non-doctor roles (state === null or `not_doctor`).
 * The 5 rendered states:
 *   active           — quiet, "Active — next bill on <date>"
 *   trialing         — informational, "Trial — N days left"
 *   grace            — CTA, "Activate before <date>"
 *   past_due         — warning, "Update payment method"
 *   never_subscribed — CTA, "Start your subscription"
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { apiClient, type DoctorSubscriptionState } from '../api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const FLAT_TIER_ID = 'doctor_monthly';
const PERCENT_TIER_ID = 'doctor_percent';

const PLAN_LABELS: Record<string, string> = {
  [FLAT_TIER_ID]: '$150/month flat',
  [PERCENT_TIER_ID]: '3% of revenue',
};

interface Props {
  state: DoctorSubscriptionState | null;
  /** Called after a successful plan switch so the parent can refetch
   *  subscription status. */
  onPlanChanged?: () => void;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export function DoctorSubscriptionCard({ state, onPlanChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [switching, setSwitching] = useState(false);

  if (!state || state.reason === 'not_doctor') {
    return null;
  }

  // Admins bypass the subscription gate but still see the same doctor UI —
  // render an informational card so support can immediately see what a
  // regular doctor sees, without any subscribe/manage CTA.
  if (state.reason === 'admin') {
    return (
      <Card className="mb-4 border-[#3A2718]/15 bg-[#EAE0CB]/50">
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-[#3A2718]">Admin — subscription bypass</div>
            <div className="text-sm mt-0.5 text-[#3A2718]/85">
              You have full doctor-dashboard access via your admin role. No subscription required.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const startCheckout = async (tierId: string = FLAT_TIER_ID) => {
    setBusy(true);
    try {
      const { url } = await apiClient.createSubscriptionCheckout(tierId);
      window.location.href = url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start checkout');
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { url } = await apiClient.createBillingPortal();
      window.location.href = url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to open billing portal');
      setBusy(false);
    }
  };

  // Style + copy per reason.
  const config = (() => {
    switch (state.reason) {
      case 'active': {
        const dateStr = formatDate(state.currentPeriodEnd);
        return {
          tone: 'ok' as const,
          title: 'Subscription active',
          body: dateStr ? `Next bill: ${dateStr}` : 'You have full dashboard access.',
          ctaLabel: 'Manage billing',
          ctaAction: openPortal,
        };
      }
      case 'trialing': {
        const days = daysUntil(state.currentPeriodEnd);
        return {
          tone: 'ok' as const,
          title: 'Free trial in progress',
          body:
            days !== null
              ? `${days} day${days === 1 ? '' : 's'} left in your trial.`
              : 'Trial active.',
          ctaLabel: 'Manage billing',
          ctaAction: openPortal,
        };
      }
      case 'grace': {
        const days = daysUntil(state.graceEndsAt);
        return {
          tone: 'warn' as const,
          title: 'Activate your ExpertMD subscription',
          body:
            days !== null
              ? `You have ${days} day${days === 1 ? '' : 's'} to activate before dashboard access requires a subscription.`
              : 'Activate before your grace period ends.',
          ctaLabel: 'Start 30-day free trial',
          ctaAction: () => startCheckout(),
        };
      }
      case 'past_due': {
        return {
          tone: 'danger' as const,
          title: 'Payment failed — update your card',
          body: 'Your last payment did not go through. Update your payment method to keep dashboard access.',
          ctaLabel: 'Update payment',
          ctaAction: openPortal,
        };
      }
      default: {
        return {
          tone: 'danger' as const,
          title:
            state.reason === 'expired' ? 'Grace period ended' : 'Start your ExpertMD subscription',
          body:
            state.reason === 'expired'
              ? 'Dashboard access requires an active subscription.'
              : '$150/month with a 30-day free trial. Full dashboard, billing, and team features included.',
          ctaLabel: 'Start 30-day free trial',
          ctaAction: () => startCheckout(),
        };
      }
    }
  })();

  // Plan switching — available while the subscription is active/trialing.
  const currentTier = state.tier ?? null;
  const otherTier = currentTier === PERCENT_TIER_ID ? FLAT_TIER_ID : PERCENT_TIER_ID;
  const canSwitch =
    (state.reason === 'active' || state.reason === 'trialing') && !!PLAN_LABELS[currentTier ?? ''];

  const switchPlan = async () => {
    const confirmed = window.confirm(
      otherTier === PERCENT_TIER_ID
        ? 'Switch to the 3% of revenue plan? Instead of $150/month, you will be billed 3% of all payments collected through ExpertMD (cards and checks) monthly to your card on file.'
        : 'Switch to the flat $150/month plan? You will be billed $150/month to your card on file, and the 3% revenue fee stops accruing.',
    );
    if (!confirmed) return;
    setSwitching(true);
    try {
      await apiClient.switchSubscriptionPlan(otherTier);
      toast.success(`Plan switched to ${PLAN_LABELS[otherTier]}.`);
      onPlanChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to switch plan');
    } finally {
      setSwitching(false);
    }
  };

  const toneClass =
    config.tone === 'ok'
      ? 'border-green-200 bg-green-50'
      : config.tone === 'warn'
        ? 'border-amber-200 bg-amber-50'
        : 'border-red-200 bg-red-50';
  const titleClass =
    config.tone === 'ok'
      ? 'text-green-900'
      : config.tone === 'warn'
        ? 'text-amber-900'
        : 'text-red-900';
  const bodyClass =
    config.tone === 'ok'
      ? 'text-green-700'
      : config.tone === 'warn'
        ? 'text-amber-800'
        : 'text-red-800';
  const btnClass =
    config.tone === 'ok'
      ? 'bg-green-600 hover:bg-green-700 text-white'
      : config.tone === 'warn'
        ? 'bg-amber-600 hover:bg-amber-700 text-white'
        : 'bg-red-600 hover:bg-red-700 text-white';

  // Admin-granted subscription discount (percent-off recurring). Only shown
  // when the subscription is in a chargeable state — grace/expired doctors
  // don't yet pay, so the discount line would be confusing there.
  const discountPercentOff = state.discountPercentOff ?? null;
  const showDiscount =
    discountPercentOff != null &&
    discountPercentOff > 0 &&
    (state.reason === 'active' || state.reason === 'trialing' || state.reason === 'past_due');
  const effectiveMonthly = showDiscount ? 150 * (1 - (discountPercentOff ?? 0) / 100) : null;

  return (
    <Card className={`mb-4 ${toneClass}`}>
      <CardContent className="p-4 flex items-center justify-between gap-4">
        <div>
          <div className={`font-semibold ${titleClass}`}>{config.title}</div>
          <div className={`text-sm mt-0.5 ${bodyClass}`}>{config.body}</div>
          {currentTier && PLAN_LABELS[currentTier] ? (
            <div className={`text-xs mt-1 ${bodyClass}`}>
              Current plan: {PLAN_LABELS[currentTier]}
              {currentTier === PERCENT_TIER_ID
                ? ' — 3% of all payments through ExpertMD (cards and checks), billed monthly to your card on file.'
                : ''}
            </div>
          ) : null}
          {showDiscount && effectiveMonthly != null ? (
            <div className={`text-xs mt-1 font-medium ${bodyClass}`}>
              Discount: {discountPercentOff}% off — ${effectiveMonthly.toFixed(2)}/mo starting next
              billing cycle.
            </div>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            onClick={config.ctaAction}
            disabled={busy || switching}
            className={`whitespace-nowrap ${btnClass}`}
          >
            {busy ? 'Redirecting…' : config.ctaLabel}
          </Button>
          {canSwitch ? (
            <button
              type="button"
              onClick={switchPlan}
              disabled={busy || switching}
              className={`text-xs underline underline-offset-2 disabled:opacity-50 ${bodyClass}`}
            >
              {switching ? 'Switching…' : `Switch to ${PLAN_LABELS[otherTier]}`}
            </button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
